import { useRef } from 'react';
import { FieldValues, useForm, UseFormProps, UseFormReturn } from 'react-hook-form';

const deepTrim = (value: unknown): unknown => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(deepTrim);
  if (value && typeof value === 'object' && (value as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, deepTrim(v)]),
    );
  }
  return value;
};

export const useTrimmedForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
>(
  props?: UseFormProps<TFieldValues, TContext, TTransformedValues>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  const form = useForm<TFieldValues, TContext, TTransformedValues>(props);
  const originalHandleSubmit = useRef(form.handleSubmit);

  // react-hook-form >=7.5x makes handleSubmit generic over TResult (returns
  // Promise<Awaited<TResult> | undefined>). A plain arrow can't express that
  // generic, so we build the wrapper and cast back to the original signature.
  const wrappedHandleSubmit = ((onValid, onInvalid) => async (e) => {
    const trimmed = deepTrim(form.getValues()) as TFieldValues;
    form.reset(trimmed, {
      keepDirty: true,
      keepTouched: true,
      keepErrors: true,
      keepIsSubmitted: true,
      keepSubmitCount: true,
      keepIsValid: true,
      keepDefaultValues: true,
    });
    return originalHandleSubmit.current(onValid, onInvalid)(e);
  }) as typeof form.handleSubmit;

  return Object.assign(form, { handleSubmit: wrappedHandleSubmit });
};
